/*
 * itv-license.js — canonical iTechVista licensing client (product-aware)
 *
 * Supersedes g-serp-license.js, which is SERP-specific: it hardcodes
 * STORE_KEY = 'itv_serp_license' and sends no product on activate. Authored
 * here rather than as a private copy for the X tool because two divergent
 * clients make a suite licence impossible later, and a suite licence anchored
 * by SERP is the direction the product is going.
 *
 * The public surface is IDENTICAL to g-serp-license.js — isPro / tier /
 * isStale / boot / activate / signOut — so the SERP section adopts this by
 * swapping one script tag, with no change to g-serp-app.js. Until it does,
 * both clients coexist; see the migration note below for why that is safe.
 *
 * Threat model, honestly: this is client-side JavaScript and a determined
 * developer can patch the bundle in their own browser. What it buys is that
 * keys are per-buyer, sharing is attributable and costs the sharer their
 * seats, and revocation is a status flip rather than a redeploy.
 */
(function (window) {
    'use strict';

    var CFG = window.ITV_LICENSE_CONFIG || {};

    /*
     * ONE key for every tool, deliberately.
     *
     * Per-tool keys would mean a suite licence needs a separate activation per
     * tool, burning two of three seats before the customer has opened the
     * second tool. Entitlement is decided SERVER-SIDE from the token's claims
     * against the requested product; the browser just carries the token.
     */
    var STORE_KEY = 'itv_license';

    /*
     * ---- MIGRATION, and the reason it COPIES rather than MOVES -------------
     *
     * Existing SERP customers hold a token under 'itv_serp_license'. If this
     * client simply read the new key it would find nothing, and every current
     * Pro user would silently drop to free. That failure is FAIL-CLOSED and it
     * is invisible to any test that only exercises a fresh activation — the
     * new-user path works perfectly while every existing customer is locked
     * out. So it is tested explicitly, from a pre-seeded old-format record.
     *
     * The legacy record is COPIED, never deleted. g-serp-license.js is still
     * live on the SERP page and still reads 'itv_serp_license'; moving the
     * token would fix this tool by breaking that one. Both clients read their
     * own key, both find a valid token, and the server sees the same token
     * from both — which is correct, because it IS the same licence on the same
     * device. The legacy key can be swept only once nothing reads it.
     */
    var LEGACY_KEYS = ['itv_serp_license'];

    /* Which product this page is asking about. Sent on activate and verify so
       entitlement is decided server-side per product. */
    var PRODUCT = CFG.product || 'serp';

    var GRACE_DAYS = 7;
    var PUBLIC_JWK = window.ITV_LICENSE_PUBLIC_JWK || null;

    var pro = false;

    // ---------------------------------------------------------------------
    // Storage
    // ---------------------------------------------------------------------

    function readKey(k) {
        try {
            var raw = window.localStorage.getItem(k);
            return raw ? JSON.parse(raw) : null;
        } catch (e) { return null; }
    }

    /*
     * NOTE: migration happens HERE, as a side effect of a read.
     *
     * load() is called from tier(), deviceId() and boot(), so merely reading
     * the tier writes a copy of the legacy record forward. That is harmless —
     * the adopted record is identical and the write is idempotent — but it
     * means "when did this user migrate" is not answerable from the call
     * graph, because the answer is "whenever anything first read anything".
     *
     * Left as-is deliberately rather than moved into boot(): migrating only in
     * boot() would mean any path that reads before boot() completes sees no
     * record and reports not-Pro, which is a fail-CLOSED against a paying
     * customer. A side-effecting read is the lesser problem, but it should be
     * a known one rather than a surprise.
     */
    function load() {
        var rec = readKey(STORE_KEY);
        if (rec && rec.token) return rec;

        // No record under the current key — adopt a legacy one if present.
        for (var i = 0; i < LEGACY_KEYS.length; i++) {
            var old = readKey(LEGACY_KEYS[i]);
            if (old && old.token) {
                var adopted = {
                    token: old.token,
                    tier: old.tier || null,
                    device: old.device || null,
                    lastVerifiedAt: old.lastVerifiedAt || null,
                    migratedFrom: LEGACY_KEYS[i]
                };
                save(adopted);          // copy forward; legacy key left in place
                return adopted;
            }
        }
        return rec;                     // may be null, or a record without a token
    }

    function save(rec) {
        try {
            window.localStorage.setItem(STORE_KEY, JSON.stringify(rec));
        } catch (e) { /* private mode: the tool still runs, just not remembered */ }
    }

    /*
     * clear() MUST sweep the legacy keys too, and this is not a tidy-up.
     *
     * ---- The fail-open this closes -------------------------------------
     *
     * An earlier version removed only STORE_KEY, on the reasoning documented
     * at LEGACY_KEYS: the legacy record is COPIED, never moved, so
     * g-serp-license.js keeps working. That reasoning is right for MIGRATION
     * and wrong for REVOCATION, and the difference cost a working kill switch:
     *
     *   1. A revoked customer boots online. verify returns valid:false, we set
     *      pro=false and clear(). Correct so far.
     *   2. clear() removed 'itv_license' and LEFT 'itv_serp_license' — still
     *      holding a signature-valid, unexpired token.
     *   3. Next launch OFFLINE. load() finds no 'itv_license', so it ADOPTS the
     *      legacy record and saves it forward.
     *   4. verify is unreachable, so boot() falls to withinGrace(). The
     *      signature verifies, exp is in the future, lastVerifiedAt is inside
     *      the 7-day window. pro = true, FOR A CUSTOMER WE REVOKED.
     *
     * And because step 2 never touched the legacy key, step 3 repeated on
     * EVERY launch. Not a one-off resurrection — it re-armed itself for as long
     * as the token's expiry and the grace window allowed.
     *
     * Revocation is the only kill switch for a refund, a chargeback or a shared
     * key, and this made it conditional on the customer staying online. The
     * person motivated to go offline is exactly the person who was revoked.
     *
     * ---- Why sweeping is safe ------------------------------------------
     *
     * The copy-not-move rule exists so a MIGRATION does not break SERP's
     * client. But clear() runs at exactly two moments, and at both of them
     * there is nothing left to preserve:
     *
     *   - an explicit server refusal, which SERP's client would receive too
     *   - sign-out, which releases the seat for every product
     *
     * A licence is suite-wide, so revoking or releasing it is suite-wide as
     * well. Leaving SERP's copy alive after either would not be preserving a
     * migration; it would be preserving the bypass.
     *
     * ---- The general lesson --------------------------------------------
     *
     * A migration that leaves the old record in place creates a SECOND,
     * UNMANAGED SOURCE OF AUTHORITY. Every path that clears the new one has to
     * clear the old one too, or the old one wins — and it wins silently, on the
     * offline path, where no online test can see it.
     */
    function clear() {
        try {
            window.localStorage.removeItem(STORE_KEY);
            for (var i = 0; i < LEGACY_KEYS.length; i++) {
                window.localStorage.removeItem(LEGACY_KEYS[i]);
            }
            // Legacy pro flags from the pre-token scheme, swept on every clear.
            window.localStorage.removeItem('itech_pro_unlocked');
            window.localStorage.removeItem('saved_passcode');
        } catch (e) { /* ignore */ }
    }

    /*
     * A stable-enough per-browser id. Deliberately NOT a fingerprint: this is
     * seat counting, not tracking. Clearing storage costs the user a seat until
     * it is released, which is why seats are releasable and the limit is
     * server-side adjustable.
     */
    function deviceId() {
        var rec = readKey(STORE_KEY) || {};
        if (rec.device) return rec.device;

        // Reuse a legacy device id if one exists, so migrating does NOT look
        // like a new device and burn a second seat.
        for (var i = 0; i < LEGACY_KEYS.length; i++) {
            var old = readKey(LEGACY_KEYS[i]);
            if (old && old.device) {
                rec.device = old.device;
                save(rec);
                return rec.device;
            }
        }

        var id;
        if (window.crypto && window.crypto.randomUUID) {
            id = window.crypto.randomUUID();
        } else if (window.crypto && window.crypto.getRandomValues) {
            var b = new Uint8Array(16);
            window.crypto.getRandomValues(b);
            id = Array.prototype.map.call(b, function (x) {
                return ('0' + x.toString(16)).slice(-2);
            }).join('');
        } else {
            id = 'fallback-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        }
        rec.device = id;
        save(rec);
        return id;
    }

    function deviceLabel() {
        var ua = navigator.userAgent || '';
        var os = /Windows/.test(ua) ? 'Windows'
               : /Android/.test(ua) ? 'Android'
               : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
               : /Mac OS X/.test(ua) ? 'macOS'
               : /Linux/.test(ua) ? 'Linux' : 'Unknown';
        var br = /Edg\//.test(ua) ? 'Edge'
               : /OPR\//.test(ua) ? 'Opera'
               : /Chrome\//.test(ua) ? 'Chrome'
               : /Firefox\//.test(ua) ? 'Firefox'
               : /Safari\//.test(ua) ? 'Safari' : 'Browser';
        return br + ' on ' + os;
    }

    // ---------------------------------------------------------------------
    // Transport
    // ---------------------------------------------------------------------

    function configured() {
        return !!(CFG.apiUrl && CFG.anonKey);
    }

    function call(fn, body) {
        if (!configured()) {
            return Promise.reject({ unreachable: false, unconfigured: true });
        }
        return fetch(CFG.apiUrl.replace(/\/+$/, '') + '/functions/v1/' + fn, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'apikey': CFG.anonKey,
                'Authorization': 'Bearer ' + CFG.anonKey
            },
            body: JSON.stringify(body)
        }).then(function (res) {
            // A 5xx means the server is there but broken. Treat it as
            // unreachable so an outage does not lock out paying customers,
            // rather than as a refusal.
            if (res.status >= 500) {
                return res.text().then(function () {
                    return Promise.reject({ unreachable: true });
                });
            }
            return res.json().then(function (data) {
                return { status: res.status, data: data };
            }, function () {
                return { status: res.status, data: {} };
            });
        }, function () {
            return Promise.reject({ unreachable: true });
        });
    }

    // ---------------------------------------------------------------------
    // Offline verification, for the grace window only
    // ---------------------------------------------------------------------

    function b64urlBytes(s) {
        var p = s.replace(/-/g, '+').replace(/_/g, '/');
        while (p.length % 4) p += '=';
        var bin = atob(p);
        var out = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
    }

    /*
     * Verifies the token's SIGNATURE locally. An earlier design in the SERP
     * client granted grace on the mere presence of a token plus a stored
     * lastVerifiedAt — both attacker-controlled, so writing a fake record and
     * blocking the licence host granted Pro forever. A forged record now fails
     * signature verification and grace is refused.
     */
    function verifyOffline(token) {
        if (!PUBLIC_JWK || !token || !window.crypto || !window.crypto.subtle) {
            return Promise.resolve(null);
        }
        var parts = String(token).split('.');
        if (parts.length !== 3) return Promise.resolve(null);

        return window.crypto.subtle.importKey(
            'jwk', PUBLIC_JWK, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']
        ).then(function (key) {
            var data = new TextEncoder().encode(parts[0] + '.' + parts[1]);
            return window.crypto.subtle.verify(
                { name: 'ECDSA', hash: 'SHA-256' }, key, b64urlBytes(parts[2]), data
            );
        }).then(function (ok) {
            if (!ok) return null;
            try {
                return JSON.parse(new TextDecoder().decode(b64urlBytes(parts[1])));
            } catch (e) { return null; }
        })['catch'](function () { return null; });
    }

    function withinGrace(rec) {
        if (!rec || !rec.token) return Promise.resolve(false);
        return verifyOffline(rec.token).then(function (claims) {
            if (!claims || !claims.exp) return false;
            var now = Date.now();
            if (now >= claims.exp * 1000) return false;      // genuinely expired
            if (rec.lastVerifiedAt &&
                (now - rec.lastVerifiedAt) > GRACE_DAYS * 86400000) return false;

            /*
             * THERE IS DELIBERATELY NO PRODUCT CHECK HERE. Do not add one.
             *
             * An earlier version of this function contained:
             *
             *     if (claims.products && claims.products.indexOf(PRODUCT) === -1 &&
             *         claims.products.indexOf('suite') === -1) return false;
             *
             * It was dormant, because tokens carry no `products` claim today —
             * and that is exactly what made it dangerous. The moment anyone adds
             * a `products` claim (a reasonable thing to do for analytics), that
             * line would start REVOKING OFFLINE GRACE for any customer whose
             * token did not happen to list the tool they were using.
             *
             * Three things make it the worst possible place for such a check:
             *   - it is fail-CLOSED, against paying customers
             *   - it fires ONLY when the licence server is unreachable, which is
             *     precisely the moment grace exists to cover
             *   - no test of the online path can see it
             *
             * A licence is SUITE-WIDE. `product` is PROVENANCE — a record of
             * what was bought, for support and analytics — and is NOT a scope
             * check. Every key ever minted was minted as "serp", so enforcing
             * scope anywhere would revoke access for every existing paying
             * customer on every other tool.
             *
             * The client still SENDS `product` on activate and verify. That is
             * correct: the client should declare what it is, so per-product
             * analytics are possible later without a schema change. Sending it
             * is not the same as it being a permission.
             */
            return true;
        });
    }

    // ---------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------

    var ITVLicense = {

        isPro: function () { return pro; },

        product: function () { return PRODUCT; },

        tier: function () {
            var rec = load();
            return (rec && rec.tier) || null;
        },

        /* True when running on an unverified but still-granted token, i.e. the
           server could not be reached. Lets the UI say so honestly. */
        isStale: function () {
            var rec = load();
            return pro && !!rec && !!rec.lastVerifiedAt &&
                   (Date.now() - rec.lastVerifiedAt) > 86400000;
        },

        /* Was this session's record adopted from an older key? Used by the
           migration test, and by the UI if it ever wants to say so. */
        migratedFrom: function () {
            var rec = readKey(STORE_KEY);
            return (rec && rec.migratedFrom) || null;
        },

        /** Boot check. Never rejects — the tool must load regardless. */
        boot: function () {
            var rec = load();

            if (!rec || !rec.token) {
                pro = false;
                return Promise.resolve(false);
            }

            return call('verify', { token: rec.token, product: PRODUCT }).then(function (out) {
                var d = out.data || {};

                if (d.valid) {
                    pro = true;
                    rec.tier = d.tier || rec.tier;
                    if (d.refreshed) rec.token = d.refreshed;
                    rec.lastVerifiedAt = Date.now();
                    save(rec);
                    return true;
                }

                // An explicit refusal. Lock immediately — do NOT fall to grace.
                pro = false;
                clear();
                return false;

            }, function (err) {
                // Could not ask. Being unable to ask is not being told no —
                // honour grace, but only against a signature we can verify.
                if (!err || !err.unreachable) {
                    pro = false;
                    return false;
                }
                return withinGrace(rec).then(function (ok) {
                    pro = ok;
                    return ok;
                });
            });
        },

        /** Exchange a customer key for a device-bound token. */
        activate: function (key) {
            var trimmed = (key || '').trim();
            if (!trimmed) {
                return Promise.resolve({ ok: false, error: 'bad_request',
                                         message: 'Enter your key first.' });
            }

            return call('activate', {
                key: trimmed,
                product: PRODUCT,            // <- the addition this file exists for
                device_hash: deviceId(),
                platform: 'web',
                device_label: deviceLabel()
            }).then(function (out) {
                var d = out.data || {};

                if (out.status === 200 && d.token) {
                    var rec = load() || {};
                    rec.token = d.token;
                    rec.tier = d.tier;
                    rec.device = rec.device || deviceId();
                    rec.lastVerifiedAt = Date.now();
                    save(rec);
                    try {
                        window.localStorage.removeItem('itech_pro_unlocked');
                        window.localStorage.removeItem('saved_passcode');
                    } catch (e) { /* ignore */ }
                    pro = true;
                    return { ok: true, tier: d.tier, seats: d.seats,
                             message: d.notice || undefined };
                }

                return {
                    ok: false,
                    error: d.error || 'invalid_key',
                    message: d.message || 'That key could not be verified.',
                    seats: d.seats
                };

            }, function (err) {
                if (err && err.unconfigured) {
                    return { ok: false, error: 'unconfigured',
                             message: 'Licensing is not configured for this store.' };
                }
                return { ok: false, error: 'unreachable',
                         message: "Couldn't reach the licence server. Check your connection and try again." };
            });
        },

        /** Release this device's seat so the user can activate elsewhere. */
        signOut: function () {
            var rec = load();
            pro = false;
            clear();
            if (rec && rec.token) {
                // Best effort: a failed release leaves the seat to be reaped
                // server-side and must never block the local sign-out.
                call('verify', { token: rec.token, product: PRODUCT, release: true })
                    ['catch'](function () {});
            }
            return Promise.resolve();
        }
    };

    /*
     * ---- Tamper friction, NOT security ------------------------------------
     *
     * The whole tool runs in the visitor's browser, so its gates are on the
     * attacker's machine and cannot be made safe. What CAN be removed is the
     * one-line console bypass — someone who asks an AI "how do I unlock this"
     * and pastes the answer. Freezing turns `ITVLicense.isPro = () => true`
     * and `window.ITVLicense = {...}` into no-ops. Anyone willing to run a
     * userscript still wins, and always will.
     *
     * `pro` is a closure variable and was never reachable from the console;
     * this only closes the door on replacing the accessors around it.
     */
    Object.freeze(ITVLicense);
    try {
        Object.defineProperty(window, 'ITVLicense', {
            value: ITVLicense,
            writable: false,
            configurable: false,
            enumerable: true
        });
    } catch (e) {
        window.ITVLicense = ITVLicense;
    }

    // Test harness access (node). Never used in the browser.
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = { ITVLicense: ITVLicense, STORE_KEY: STORE_KEY, LEGACY_KEYS: LEGACY_KEYS };
    }
})(typeof window !== 'undefined' ? window : globalThis);
